/**
 * Thin shim over the shared llms.txt generator in `@sill/types`.
 *
 * The dashboard previously duplicated the edge generator's logic here
 * so the two could not drift. The generator now lives in
 * `@sill/types/llms-txt`; both the edge AND the dashboard import it
 * directly, so the preview is byte-identical to what the edge serves.
 *
 * The exported `generateLlmsTxtPreview` keeps the previous name for
 * import-call parity and forwards to the shared generator. The input
 * shape is the shared `LlmsTxtGeneratorInput` — in particular, the
 * caller must pass `is_store` so a website preview omits the
 * store-only Discovery lines (AI catalog, UCP profile).
 */

import { generateLlmsTxt } from '@sill/types';
import type { LlmsTxtGeneratorInput } from '@sill/types';

/**
 * One capability entry for the preview. Mirror of the shared
 * `AgentCardCapability` shape; kept structural so callers can map
 * their own skill rows without widening to the full skill type.
 */
export interface LlmsPreviewCapability {
  readonly name: string;
  readonly description: string;
}

/**
 * Input shape for {@link generateLlmsTxtPreview}. Structural mirror of
 * `LlmsTxtGeneratorInput`. `capabilities` accepts the narrower
 * {@link LlmsPreviewCapability} shape; the shared generator only reads
 * `name` + `description` for the MCP capabilities section.
 */
export interface LlmsPreviewInput {
  readonly site_key_hash: string;
  readonly domain: string;
  readonly display_name: string;
  readonly capabilities: readonly LlmsPreviewCapability[];
  readonly description?: string;
  readonly custom_sections?: string;
  readonly real_domain?: boolean;
  readonly is_store?: boolean;
  /** Emit the PAP discovery line (edge: `papEnabled(env)`). */
  readonly pap_enabled?: boolean;
  readonly facts?: LlmsTxtGeneratorInput['facts'];
  readonly policies?: LlmsTxtGeneratorInput['policies'];
}

/**
 * Build the llms.txt Markdown preview for `input`. Thin forwarder to
 * the shared generator — same bytes as the edge.
 */
export function generateLlmsTxtPreview(input: LlmsPreviewInput): string {
  const capabilities = input.capabilities.map((c) => ({
    // The shared generator only reads `name` + `description`; `skill_id`
    // and `auth_required` are present on the signed AgentCardCapability
    // but the generator does not emit them, so we pass structural
    // placeholders here. The widening is safe because `is_store` /
    // `real_domain` on the input control what gets emitted, not the
    // capability fields.
    skill_id: c.name as unknown as import('@sill/types').SkillId,
    name: c.name,
    description: c.description,
    auth_required: false,
  }));
  return generateLlmsTxt({
    site_key_hash: input.site_key_hash,
    domain: input.domain,
    display_name: input.display_name,
    capabilities,
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.custom_sections !== undefined ? { custom_sections: input.custom_sections } : {}),
    ...(input.real_domain !== undefined ? { real_domain: input.real_domain } : {}),
    ...(input.is_store !== undefined ? { is_store: input.is_store } : {}),
    ...(input.pap_enabled !== undefined ? { pap_enabled: input.pap_enabled } : {}),
    ...(input.facts !== undefined ? { facts: input.facts } : {}),
    ...(input.policies !== undefined ? { policies: input.policies } : {}),
  });
}

/** Preview-only default when the merchant has cleared their override. */
export function defaultLlmsDescription(domain: string, is_store = true): string {
  return is_store
    ? `AI-ready commerce endpoint for ${domain}.`
    : `Information and agent endpoints for ${domain}.`;
}
